<script setup lang="ts">
/**
 * Sidebar droite de la page adhésion — parité TOTALE avec BslugSidebar
 * (Karim 2026-06-21) :
 *  - Wrapper sticky (lg:sticky lg:top-6)
 *  - Location Card en HAUT si adresse présente
 *  - Carte « Détails de votre commande » (apparait au step récap) :
 *      header + lignes plan/options + input code promo + réduction + frais
 *      + soutien Donoor + total à payer
 *  - Carte branding Donoor en bas si pas d'adresse
 *
 * Le bloc « info plan » séparé (durée, places restantes, count) a été retiré
 * pour éviter le doublon visuel avec « Détails de votre commande ». Les
 * informations utiles (places restantes) sont intégrées en bas de la carte
 * récap quand pertinent.
 */
import { fraisDesParts, type PartDuPanier } from "~/utils/plusieursFois";
import PublicPageTotauxPlusieursFois from "~/components/public-page/PublicPageTotauxPlusieursFois.vue";
import { lienTelephone, telephoneAffiche, texteItineraireLieu } from "@donoor/utils";
import type { MembershipPlanPublic, MembershipPlanOption, MembershipCustomField } from "~/types/membership";
import { nomDuTarif } from "~/utils/tarifAdhesion";

const { formatCurrency } = useFormatLangue();

type SiblingPlan = MembershipPlanPublic & {
  options?: MembershipPlanOption[];
  custom_fields?: MembershipCustomField[];
};

const props = defineProps<{
  plan: MembershipPlanPublic;
  hasMultiplePlans: boolean;
  allPlans: SiblingPlan[];
  planQuantities: Record<string, number>;
  quantity: number;
  durationLabel: string;
  totalPrice: number;
  checkoutStep: number;
  stepRecap: number;
  totalPlanPrice: number;
  totalOptionsPrice: number;
  subtotal: number;
  promoDiscount: number;
  promoApplied: { code: string; discount_type: string; discount_value: number } | null;
  /** Promo input live state (parité /b — l'input vit dans la sidebar). */
  promoCode: string;
  promoLoading: boolean;
  promoError: string;
  feesAmount: number;
  includeFees: boolean;
  donateUs: boolean;
  donateToUsAmount: number;
  /** Don à l'association (euros) — hors adhésion, hors commission. */
  donationAmount?: number;
  /** Parts du paiement en plusieurs fois choisi (vide : en une fois). */
  partsPlusieursFois?: PartDuPanier[];
}>();

// En plusieurs fois : frais de TOUTE la commande (un frais fixe par
// paiement) et bas du détail = total réel, aujourd'hui, puis la suite.
const enPlusieursFois = computed(() => (props.partsPlusieursFois?.length ?? 0) >= 2);
const fraisAffiches = computed(() =>
  enPlusieursFois.value
    ? fraisDesParts(props.partsPlusieursFois ?? [], props.totalPrice + don.value + (props.donateUs ? props.donateToUsAmount : 0))
    : props.feesAmount,
);

const don = computed(() => Math.max(0, props.donationAmount ?? 0));

defineEmits<{
  (e: 'update:promo-code', v: string): void;
  (e: 'apply-promo' | 'remove-promo'): void;
}>();

const hasLocation = computed(() =>
  Boolean(props.plan?.location_name || props.plan?.location_address || props.plan?.location_city),
);

// Texte de la carte : adresse + ville sans doublon, sinon le nom du lieu —
// règle partagée `texteItineraireLieu`, MÊME texte d'itinéraire que le
// dashboard et le mobile. La carte n'en a besoin qu'en repli : le point
// ENREGISTRÉ du lieu (lot 4) passe avant, sans géocodage.
const fullAddress = computed(() =>
  texteItineraireLieu(props.plan?.location_address, props.plan?.location_city, props.plan?.location_name),
);

// Lignes d'adresse dédupliquées (name/address/city se chevauchent souvent :
// address contient déjà la ville → city ne doit pas réapparaître). Karim 2026-07-08.
const addressLines = computed(() => buildAddressLines(props.plan ?? {}));
</script>

<template>
  <div class="lg:w-[320px] shrink-0 mt-0 md:mt-4 lg:mt-6 pb-6">
    <div class="lg:sticky lg:top-6 space-y-4 md:space-y-6">

      <!-- Location Card (en HAUT si adresse, parité /b/[slug]) -->
      <div v-if="hasLocation" class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
        <ClientOnly>
          <MiniMap
            :address="fullAddress"
            :latitude="plan.location_latitude"
            :longitude="plan.location_longitude"
            :precision="plan.location_precision"
            :pays="plan.location_country || plan.country"
            :territoire="plan.location_territory"
            :departement="plan.location_department"
            :ville-lieu="plan.location_city"
            height="180px"
          />
          <template #fallback>
            <div class="h-[180px] bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
              <UIcon name="i-lucide-map" class="size-10 text-gray-400 dark:text-gray-600" />
            </div>
          </template>
        </ClientOnly>
        <div class="p-5 space-y-1">
          <p
            v-for="(line, i) in addressLines"
            :key="i"
            class="text-sm"
            :class="i === 0 ? 'font-semibold text-[#2d2e5c] dark:text-white' : 'text-[#505870] dark:text-gray-400'"
          >
            {{ line }}
          </p>
        </div>
        <div v-if="plan.contact_email || plan.contact_phone" class="border-t border-gray-100 dark:border-gray-800 p-5">
          <p class="text-xs text-[#717198] dark:text-gray-500 mb-1">{{ $t('adhesion.aslugSidebar.uneQuestion') }}</p>
          <p class="text-xs text-[#717198] dark:text-gray-500">{{ $t('adhesion.aslugSidebar.contactezOrganisateur') }}</p>
          <a v-if="plan.contact_email" :href="`mailto:${plan.contact_email}`" class="block mt-2 text-sm font-medium text-[#212121] dark:text-white hover:underline">{{ plan.contact_email }}</a>
          <a v-if="plan.contact_phone" :href="lienTelephone(plan.contact_phone, plan.country)" class="block mt-1 text-sm font-medium text-[#212121] dark:text-white hover:underline">{{ telephoneAffiche(plan.contact_phone, plan.country) }}</a>
        </div>
      </div>

      <!-- Order summary — parité TOTALE avec /b/[slug] : visible dès le step 2
           (une fois la formule choisie), footer « Sous-total » avant le récap
           puis « Total à payer » au récap, promo + breakdown réservés au récap.
           Les infos propres à l'adhésion (durée + barre « Places prises »)
           sont intégrées DANS cette carte pour rester cohérentes avec le
           système d'adhésion. Masqué sur mobile (< lg) : le récap complet vit
           déjà dans le corps au step récap, on évite le doublon. -->
      <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 translate-y-2" leave-active-class="transition-all duration-200 ease-in" leave-to-class="opacity-0 -translate-y-2">
        <div v-if="checkoutStep >= 2 && (totalPlanPrice + totalOptionsPrice) > 0" class="hidden lg:block bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
          <div class="p-5">
            <h3 class="text-base font-bold text-[#2d2e5c] dark:text-white mb-4">{{ $t('adhesion.aslugSidebar.detailsCommande') }}</h3>

            <div class="space-y-3">
              <template v-if="hasMultiplePlans">
                <div
                  v-for="ap in allPlans.filter((p) => (planQuantities[p.id] || 0) > 0)"
                  :key="ap.id"
                  class="flex items-center justify-between"
                >
                  <p class="text-sm text-[#2d2e5c] dark:text-white">
                    {{ nomDuTarif(ap) }} <span class="text-[#717198]">x{{ planQuantities[ap.id] || 0 }}</span>
                  </p>
                  <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white shrink-0 ml-3">
                    {{ formatCurrency(Number(ap.price) * (planQuantities[ap.id] || 0)) }}
                  </span>
                </div>
              </template>
              <div v-else class="flex items-center justify-between">
                <p class="text-sm text-[#2d2e5c] dark:text-white">
                  {{ plan.title }}<span v-if="quantity > 1" class="text-[#717198]"> x{{ quantity }}</span>
                </p>
                <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white shrink-0 ml-3">
                  {{ formatCurrency(totalPlanPrice) }}
                </span>
              </div>
              <div v-if="totalOptionsPrice > 0" class="flex items-center justify-between">
                <p class="text-sm text-[#717198]">{{ $t('adhesion.aslugSidebar.options') }}</p>
                <span class="text-sm text-[#717198] shrink-0 ml-3">+{{ formatCurrency(totalOptionsPrice) }}</span>
              </div>
            </div>

            <!-- Contexte adhésion : durée + nb adhérents + barre « Places prises »
                 (propre au système d'adhésion, intégré à la carte récap). -->
            <div v-if="durationLabel || quantity > 1 || plan.max_members" class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2">
              <p v-if="durationLabel" class="flex items-center gap-2 text-sm text-[#505870] dark:text-gray-400">
                <UIcon name="i-lucide-clock" class="size-4" />
                {{ durationLabel }}
              </p>
              <p v-if="quantity > 1" class="flex items-center gap-2 text-xs text-[#717198] dark:text-gray-500">
                <UIcon name="i-lucide-users" class="size-3.5" />
                {{ $t('adhesion.aslugSidebar.adherentPluriel', quantity) }}
              </p>
              <div v-if="plan.max_members">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-[#717198] dark:text-gray-500">{{ $t('adhesion.aslugSidebar.placesPrises') }}</span>
                  <span class="font-semibold text-[#2d2e5c] dark:text-white">{{ plan.member_count || 0 }} / {{ plan.max_members }}</span>
                </div>
                <div class="mt-2 h-2 bg-gray-100 dark:bg-[#1e1e1e] rounded-full overflow-hidden">
                  <div
                    class="h-full bg-emerald-500 rounded-full transition-all"
                    :style="{ width: `${Math.min(100, ((plan.member_count || 0) / plan.max_members) * 100)}%` }"
                  />
                </div>
              </div>
            </div>

            <!-- Code promo input + réduction UNIQUEMENT au step récap
                 (parité /b/[slug]). -->
            <div v-if="checkoutStep >= stepRecap && (totalPlanPrice + totalOptionsPrice) > 0" class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <PublicPagePromoCodeInput
                variant="a"
                :model-value="promoCode"
                :applied="promoApplied"
                :loading="promoLoading"
                :error="promoError"
                @update:model-value="$emit('update:promo-code', $event)"
                @apply="$emit('apply-promo')"
                @remove="$emit('remove-promo')"
              />
            </div>

            <div v-if="checkoutStep >= stepRecap && promoDiscount > 0" class="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <span class="text-sm text-emerald-600">{{ $t('adhesion.aslugSidebar.reduction') }}</span>
              <span class="text-sm font-semibold text-emerald-600">-{{ formatCurrency(promoDiscount) }}</span>
            </div>
          </div>

          <div v-if="checkoutStep >= stepRecap && ((includeFees && feesAmount > 0) || (donateUs && donateToUsAmount > 0) || don > 0)" class="px-5 pb-2 space-y-1.5">
            <div v-if="don > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ $t('adhesion.aslugSidebar.donA', { organisation: plan.organization_name || $t('adhesion.aslugSidebar.organisationParDefaut') }) }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(don) }}</span>
            </div>
            <div v-if="includeFees && fraisAffiches > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ $t('adhesion.aslugSidebar.fraisTransaction') }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(fraisAffiches) }}</span>
            </div>
            <div v-if="donateUs && donateToUsAmount > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ $t('adhesion.aslugSidebar.soutienDonoor') }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(donateToUsAmount) }}</span>
            </div>
          </div>

          <div v-if="checkoutStep >= stepRecap && enPlusieursFois" class="border-t border-gray-100 dark:border-gray-800 px-5 py-4">
            <PublicPageTotauxPlusieursFois :parts="partsPlusieursFois ?? []" />
          </div>
          <div v-else class="border-t border-gray-100 dark:border-gray-800 px-5 py-4">
            <div class="flex items-center justify-between">
              <span class="text-base font-bold text-[#2d2e5c] dark:text-white">{{ checkoutStep >= stepRecap ? $t('adhesion.aslugSidebar.totalAPayer') : $t('adhesion.aslugSidebar.sousTotal') }}</span>
              <span class="text-lg font-bold text-[#2d2e5c] dark:text-white">
                {{ checkoutStep >= stepRecap
                  ? ((totalPrice + don + (includeFees ? feesAmount : 0) + (donateUs ? donateToUsAmount : 0)) > 0
                    ? formatCurrency(totalPrice + don + (includeFees ? feesAmount : 0) + (donateUs ? donateToUsAmount : 0))
                    : $t('adhesion.aslugSidebar.gratuit'))
                  : (totalPrice > 0 ? formatCurrency(totalPrice) : $t('adhesion.aslugSidebar.gratuit')) }}
              </span>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Contact (seul, si pas d'adresse mais email/tel présents) -->
      <div v-if="!hasLocation && (plan?.contact_email || plan?.contact_phone)" class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-5">
        <p class="text-xs text-[#717198] dark:text-gray-500 mb-2">{{ $t('adhesion.aslugSidebar.contact') }}</p>
        <a v-if="plan.contact_email" :href="`mailto:${plan.contact_email}`" class="block text-sm font-medium text-[#212121] dark:text-white hover:underline">{{ plan.contact_email }}</a>
        <a v-if="plan.contact_phone" :href="lienTelephone(plan.contact_phone, plan.country)" class="block text-sm font-medium text-[#212121] dark:text-white hover:underline mt-1">{{ telephoneAffiche(plan.contact_phone, plan.country) }}</a>
      </div>

      <!-- Donoor branding card (uniquement si pas d'adresse) -->
      <PublicPageDonoorBrandingCard
        v-if="!hasLocation"
        :organization-name="plan?.organization_name"
        :action="$t('association.publicPage.brandingCard.actionAdhesion')"
      />
    </div>
  </div>
</template>
